import t from 'react-translate';

export enum ClosingReason {
  COMPLETED = 'complete',
  TERMINATED = 'terminated',
}

type StreamChunk = {
  content: string
  isLastChunk: boolean
  closingReason?: ClosingReason
};

type StreamParams = {
  aiApiEndpoint: string;
  token: string;
  userMessage: string | null;
  onChunk: (chunk: StreamChunk) => void;
  onError?: (err: any) => void;
  abortSignal?: AbortSignal;
};

// SSE event types from AI service
enum SSEEventType {
  Delta = 'delta',
  Done = 'done',
  Error = 'error',
}

type DeltaEventData = {
  content: string
};

type DoneEventData = {
  closing_reason?: ClosingReason
};

type ErrorEventData = {
  error: string
};

const streamAIResponse = async ({
  aiApiEndpoint,
  token,
  userMessage,
  onChunk,
  onError,
  abortSignal,
}: StreamParams) => {
  if (!aiApiEndpoint) {
    const error = new Error(t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.AI_HOST_URL_MISSING_ERROR());
    onError?.(error);
    throw error;
  }

  try {
    const response = await fetch(`${aiApiEndpoint}/role-play/learner/stream`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user_message: userMessage,
      }),
      signal: abortSignal,
    });

    if (!response.ok || !response.body) {
      throw new Error('Failed to connect to AI stream');
    }

    const reader = response.body
      .pipeThrough(new TextDecoderStream())
      .getReader();

    let buffer = '';

    const processSSEEvent = (eventType: SSEEventType, data: string): boolean => {
      let shouldCancel = false;

      try {
        switch (eventType) {
          case SSEEventType.Delta: {
            const deltaData: DeltaEventData = JSON.parse(data);
            onChunk({
              content: deltaData.content,
              isLastChunk: false,
            });
            break;
          }
          case SSEEventType.Done: {
            const doneData: DoneEventData = data ? JSON.parse(data) : {};
            onChunk({
              content: '',
              isLastChunk: true,
              closingReason: doneData.closing_reason,
            });
            break;
          }
          case SSEEventType.Error: {
            const errorData: ErrorEventData = JSON.parse(data);
            shouldCancel = true;
            onError?.(new Error(`Error from AI stream: ${errorData.error}`));
            break;
          }
          default:
            // Unknown event type, ignore
            break;
        }
      } catch (err) {
        onError?.(err);
      }

      return shouldCancel;
    };

    const processLines = (lines: string[]): boolean => {
      let shouldCancel = false;
      let currentEventType: SSEEventType | null = null;

      lines.forEach((rawLine) => {
        if (shouldCancel) return;

        const line = rawLine.trim();

        if (line.startsWith('event:')) {
          currentEventType = line.replace(/^event:\s*/, '') as SSEEventType;
        } else if (line.startsWith('data:')) {
          const data = line.replace(/^data:\s*/, '');
          if (currentEventType) {
            shouldCancel = processSSEEvent(currentEventType, data);
            currentEventType = null;
          }
          // Note: data lines without preceding event lines are silently dropped.
          // This is intentional defensive behavior for malformed SSE streams.
        }
      });

      return shouldCancel;
    };

    const readNextChunk = (): Promise<void> => reader.read().then(({ value, done }) => {
      if (done) return Promise.resolve();

      buffer += value;

      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      const shouldCancel = processLines(lines);

      if (shouldCancel) {
        return reader.cancel();
      }

      return readNextChunk();
    });

    await readNextChunk();
  } catch (err) {
    // Don't call onError or re-throw for aborted requests - this is intentional cancellation
    if (err instanceof Error && err.name === 'AbortError') {
      return;
    }
    onError?.(err);
    // Re-throw so callers can add their own .catch() for additional cleanup logic
    // e.g., handleStartConversation needs to reset status to NOT_STARTED and clear opening narrative
    throw err;
  }
};

export default streamAIResponse;
